import InfoIcon from "@mui/icons-material/Info";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  Link,
  TextField,
  Theme,
  Tooltip,
  Typography,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import { useTheme } from "@mui/material/styles";
import ConfirmDialog from "components/ConfirmDialog";
import ContextOrganizationField, {
  ContextOrganizationFieldMode,
} from "components/ContextOrganizationField";
import Field from "components/Field";
import FieldArray from "components/FieldArray";
import SelectProduct from "components/SelectProduct";
import SelectProductVersion from "components/SelectProductVersion";
import SelectProductYear from "components/SelectProductYear";
import SelectSku from "components/SelectSku";
import SelectSkuAvailability from "components/SelectSkuAvailability";
import { Decorator, FORM_ERROR } from "final-form";
import arrayMutators from "final-form-arrays";
import createDecorator from "final-form-calculate";
import { makeStyles } from "helpers/StyleHelper";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import { pick } from "lodash";
import PrintReferenceCheckbox from "pages/EvaluationsPage/PrintReferenceCheckbox";
import CreateQuestionForm from "pages/QuestionsPage/QuestionCreateForm";
import { useCallback, useMemo, useRef, useState } from "react";
import { FormProps, withTypes } from "react-final-form";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import {
  IdNameModel,
  IEvaluation,
  IQuestion,
  ListResponseOfEvaluationType,
  ResponseCode,
  ResponseOfIdNameModel,
  SaveEvaluationRequest,
  SaveQuestion,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import {
  EvaluationFormMode,
  EvaluationTypeIdConstants,
} from "types/common-types";
import {
  assignImportedIds,
  isImportedQuestion,
} from "utils/evaluationQuestionUtils";
import { globalContextUpdaters, parseInteger } from "utils/formUtils";
import translateError from "utils/translateError";
import DefaultOrganizationEvaluationSettingsPopulator from "./DefaultOrganizationEvaluationSettingsPopulator";
import { EvaluationImportDialog } from "./EvaluationImportDialog";
import EvaluationQuestionsTable from "./EvaluationQuestionsTable";
import QuestionSearch from "./QuestionSearch";
import SelectEvaluationStatus from "./SelectEvaluationStatus";
import SelectEvaluationType from "./SelectEvaluationType";
import SelectProductType from "./SelectProductType";

const useStyles = makeStyles((theme: Theme) => ({
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
  subtitle: {
    margin: theme.spacing(3, 0, 2),
    color: theme.palette.primary.main,
    fontWeight: "bold",
  },
  checkboxContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  lineContainer: {
    display: "flex",
    alignItems: "center",
  },
  link: {
    "&:hover": {
      textDecoration: "none",
    },
  },
}));

export type EvaluationFormValues = Omit<Partial<IEvaluation>, "questions"> & {
  questions?: IQuestion[];
};

const { Form } = withTypes<EvaluationFormValues>();

type EvaluationFormProps = {
  evaluation?: IEvaluation;
  mode: EvaluationFormMode;
  onCancel?: () => void;
};

const EvaluationForm = ({
  evaluation,
  mode,
  onCancel,
}: EvaluationFormProps & Omit<FormProps<EvaluationFormValues>, "onSubmit">) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { formatMessage: t } = useIntl();
  const styles = useStyles(useTheme());
  const { organizationId } = contextStore.useState();
  const [showDuplicateDialog, showDuplicateDialogActions] = useBoolean(false);
  const [duplicateEvaluationInfo, setDuplicateEvaluationInfo] = useState<
    IdNameModel | undefined
  >(undefined);

  const [isEvaluationTypeSet, isEvaluationTypeSetActions] = useBoolean(
    evaluation?.evaluationType != null,
  );
  const {
    evaluationService,
    evaluationDetailsUrl,
    questionService,
  } = useGlobalContext();

  const createQuestionAction = useRef<
    ((createdQuestion: IQuestion, createAnother?: boolean) => void) | null
  >(null);
  const createQuestionOrganizationId = useRef<string | null>(null);
  const importQuestionsAction = useRef<
    ((questions: IQuestion[]) => void) | null
  >(null);
  const [createOpen, createOpenActions] = useBoolean(false);
  const [importOpen, importOpenActions] = useBoolean(false);
  const [
    showInactiveConfirmation,
    showInactiveConfirmationActions,
  ] = useBoolean(false);

  const fetchedEvaluationTypes = useQuery<ListResponseOfEvaluationType>(
    ["allEvaluationTypes"],
    () => evaluationService.getEvaluationTypes(null),
  );

  const evaluationOrganizationId = evaluation?.organizationId ?? organizationId;

  const init = useMemo<EvaluationFormValues>(
    () => ({
      questions:
        evaluation?.evaluationQuestions?.map((x) => x.question as IQuestion) ??
        [],
      ...pick(evaluation ?? { isActive: true, enablePrintReference: true }, [
        "organizationId",
        "attemptsAllowed",
        "daysAfterCourse",
        "description",
        "evaluationTypeId",
        "name",
        "passingScore",
        "productTypeId",
        "productVersionId",
        "skuAvailabilityId",
        "productYearId",
        "productId",
        "deleted",
        "skuId",
        "value",
        "isActive",
        "enablePrintReference",
        "hasConfidenceLevels",
      ]),
    }),
    [evaluation],
  );

  const saveNewQuestions = useCallback(
    async (
      questions: IQuestion[],
      organizationIdForNewQuestions: string
    ): Promise<string[]> => {
      const importedQuestions = questions.filter(isImportedQuestion);

      if (importedQuestions.length === 0) {
        return questions.map((q) => q.id!);
      }

      const saveRequests = importedQuestions.map(
        (q) =>
          new SaveQuestion({
            organizationId: organizationIdForNewQuestions,
            value: q.value,
            required: q.required,
            hasConfidenceLevels: q.hasConfidenceLevels,
            questionTypeId: q.questionType?.id!,
            answers: q.evaluationQuestionAnswers,
          }),
      );

      const bulkSaveResponse = await questionService.importQuestions(
        saveRequests,
      );
      const newIds = bulkSaveResponse.data ?? [];

      let newIdIndex = 0;
      return questions.map((q) =>
        isImportedQuestion(q) ? newIds[newIdIndex++] : q.id!,
      );
    },
    [questionService],
  );

  const onSubmit = useCallback(
    async (values: EvaluationFormValues) => {
      const isNewEvaluation = !evaluation;
      if (isNewEvaluation && !showInactiveConfirmation && !values.isActive) {
        showInactiveConfirmationActions.setTrue();
        return;
      }

      try {
        const questionIds = await saveNewQuestions(
          values.questions!,
          values.organizationId as string
        );

        const evaluationType = fetchedEvaluationTypes.data!.data!.find(
          (et) => et.id === values.evaluationTypeId,
        )?.id;

        const requestData = {
          ...values,
          id: evaluation?.id,
          organizationId: values.organizationId as string,
          name: values.name as string,
          passingScore:
            evaluationType === EvaluationTypeIdConstants.Exam &&
            values.passingScore
              ? parseFloat(`${values.passingScore}`)
              : undefined,
          attemptsAllowed:
            evaluationType === EvaluationTypeIdConstants.Exam &&
            values.attemptsAllowed
              ? parseInt(`${values.attemptsAllowed}`, 10)
              : undefined,
          daysAfterCourse:
            evaluationType === EvaluationTypeIdConstants.Survey &&
            typeof values.daysAfterCourse === "number"
              ? parseInt(`${values.daysAfterCourse}`, 10)
              : undefined,
          questionIds: questionIds,
          enablePrintReference:
            values.evaluationTypeId === EvaluationTypeIdConstants.Exam
              ? values.enablePrintReference
              : undefined,
        };

        const response: ResponseOfIdNameModel = await evaluationService.saveEvaluation(
          new SaveEvaluationRequest(requestData),
        );
        const evaluationId = response?.data?.id;
        if (evaluationId) {
          queryClient.invalidateQueries("evaluations");
          queryClient.invalidateQueries(["evaluation", evaluationId]);
          navigate(evaluationDetailsUrl(evaluationId));
        }
      } catch (err: any) {
        if (err.data?.responseCode === ResponseCode.ValidationFailed) {
          setDuplicateEvaluationInfo(err.data.data as IdNameModel);
          showDuplicateDialogActions.setTrue();
        }
        return { [FORM_ERROR]: translateError(err, t) };
      }
    },
    [
      evaluation,
      showInactiveConfirmation,
      showInactiveConfirmationActions,
      fetchedEvaluationTypes.data,
      evaluationService,
      queryClient,
      navigate,
      evaluationDetailsUrl,
      t,
      showDuplicateDialogActions,
      saveNewQuestions,
    ],
  );

  const calculator = useMemo(() => {
    return createDecorator(
      ...globalContextUpdaters({}),
    ) as Decorator<EvaluationFormValues>;
  }, []);

  const submitLabel =
    mode === EvaluationFormMode.Create
      ? t({ id: "evaluationFormCreateButton" })
      : t({ id: "evaluationFormUpdateButton" });

  const handleOpenExistingEvaluation = (evaluationId: string) => {
    showDuplicateDialogActions.setFalse();
    window.open(evaluationDetailsUrl(evaluationId), "_blank");
  };

  const form = useMemo(
    () => (
      <Form
        onSubmit={onSubmit}
        validate={(values) => {
          const errors = {};
          if (!values.skuId && !values.skuAvailabilityId) {
            const errorMessage = t({
              id: "evaluationFormSkuAvailabilityOrSkuRequiredLabel",
            });
            return {
              [FORM_ERROR]: errorMessage,
              skuAvailabilityId: errorMessage,
              skuId: errorMessage,
            };
          }

          if (
            values.passingScore &&
            (values.passingScore < 0 || values.passingScore > 100)
          ) {
            return {
              passingScore: t({ id: "passingScoreValidationError" }),
            };
          }
          return errors;
        }}
        initialValues={init}
        decorators={[calculator]}
        mutators={{ ...arrayMutators }}
        render={({
          form: {
            mutators: { push },
            change,
          },
          handleSubmit,
          submitting,
          submitError,
          values,
        }) => {
          const onAddQuestion = (question: IQuestion) => {
            push("questions", question);
          };

          const onCreateQuestion = () => {
            createQuestionOrganizationId.current =
              values.organizationId as string;
            createQuestionAction.current = (
              createdQuestion: IQuestion,
              createAnother?: boolean,
            ) => {
              if (!createAnother) {
                createOpenActions.setFalse();
                createQuestionAction.current = null;
              }
              push("questions", createdQuestion);
            };

            createOpenActions.setTrue();
          };

          const handleImportedQuestions = (questions: IQuestion[]) => {
            const questionsWithIds = assignImportedIds(questions);
            questionsWithIds.forEach((q) => push("questions", q));
            importOpenActions.setFalse();
            importQuestionsAction.current = null;
          };

          const onImportQuestions = () => {
            importQuestionsAction.current = handleImportedQuestions;
            importOpenActions.setTrue();
          };

          const evaluationType = fetchedEvaluationTypes.data?.data?.find(
            (et) => et.id === values.evaluationTypeId,
          )?.id;

          const validateSkuAndSkuAvailability = () => {
            if (!values.skuId && !values.skuAvailabilityId) {
              const errorMessage = t({
                id: "evaluationFormSkuAvailabilityOrSkuRequiredLabel",
              });
              return {
                [FORM_ERROR]: errorMessage,
                skuAvailabilityId: errorMessage,
                skuId: errorMessage,
              };
            }
          };

          const isSkuRequired = !!values.skuAvailabilityId === false;
          const isSkuAvailabilityIdRequired = !!values.skuId === false;

          return (
            <form onSubmit={handleSubmit} noValidate>
              <DefaultOrganizationEvaluationSettingsPopulator
                evaluationTypeId={values.evaluationTypeId}
                skuAvailabilityId={values.skuAvailabilityId}
                organizationId={evaluationOrganizationId}
                mode={mode}
                formChange={change}
                evaluationTypes={fetchedEvaluationTypes.data}
              />
              <DialogContent dividers>
                <Grid container spacing={2}>
                  <ContextOrganizationField
                    values={values}
                    formChange={change}
                    mode={
                      mode === EvaluationFormMode.Create
                        ? ContextOrganizationFieldMode.SetByHeader
                        : ContextOrganizationFieldMode.EditManuallyTriggered
                    }
                  />
                  <Field name="evaluationTypeId" required>
                    {({ input, meta }) => (
                      <Grid item xs={12} md={6}>
                        <FormControl
                          fullWidth
                          error={meta.error && meta.submitFailed}
                        >
                          <SelectEvaluationType
                            required={true}
                            {...input}
                            label={t({
                              id: "evaluationFormEvaluationTypeLabel",
                            })}
                            parentOnChange={(v) =>
                              isEvaluationTypeSetActions.setValue(v != null)
                            }
                            textFieldProps={{
                              error: meta.error && meta.submitFailed,
                            }}
                          />
                          {meta.error && meta.submitFailed && (
                            <FormHelperText>{meta.error}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                    )}
                  </Field>

                  <Field name="isActive" required>
                    {({ input, meta }) => (
                      <Grid item xs={12} md={6}>
                        <FormControl
                          fullWidth
                          error={meta.error && meta.submitFailed}
                        >
                          <SelectEvaluationStatus
                            {...input}
                            label={t({
                              id: "evaluationFormEvaluationStatusLabel",
                            })}
                            textFieldProps={{
                              error: meta.error && meta.submitFailed,
                            }}
                          />
                          {meta.error && meta.submitFailed && (
                            <FormHelperText>{meta.error}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                    )}
                  </Field>

                  <Field name="name" required>
                    {({ input, meta }) => (
                      <Grid item xs={12}>
                        <TextField
                          variant="standard"
                          fullWidth
                          label={t({ id: "nameLabel" })}
                          InputLabelProps={{ required: true }}
                          error={meta.error && meta.submitFailed}
                          helperText={
                            meta.error && meta.submitFailed && meta.error
                          }
                          {...input}
                        />
                      </Grid>
                    )}
                  </Field>

                  <Field name="value" required>
                    {({ input, meta }) => (
                      <Grid item xs={12}>
                        <TextField
                          variant="standard"
                          fullWidth
                          label={t({ id: "valueLabel" })}
                          InputLabelProps={{ required: true }}
                          error={meta.error && meta.submitFailed}
                          helperText={
                            meta.error && meta.submitFailed && meta.error
                          }
                          {...input}
                        />
                      </Grid>
                    )}
                  </Field>

                  <Field name="description" required>
                    {({ input, meta }) => (
                      <Grid item xs={12}>
                        <TextField
                          variant="standard"
                          fullWidth
                          label={t({ id: "descriptionLabel" })}
                          InputLabelProps={{ required: true }}
                          error={meta.error && meta.submitFailed}
                          helperText={
                            meta.error && meta.submitFailed && meta.error
                          }
                          {...input}
                        />
                      </Grid>
                    )}
                  </Field>

                  <Field
                    name="skuAvailabilityId"
                    validate={validateSkuAndSkuAvailability}
                  >
                    {({ input, meta }) => (
                      <Grid item xs={12} md={6}>
                        <FormControl
                          fullWidth
                          error={meta.error && meta.submitFailed}
                          required={isSkuAvailabilityIdRequired}
                        >
                          <SelectSkuAvailability
                            {...input}
                            label={t({
                              id: "skuAvailabilityLabel",
                            })}
                            textFieldProps={{
                              error: meta.error && meta.submitFailed,
                              required: isSkuAvailabilityIdRequired,
                            }}
                          />
                          {meta.error && meta.submitFailed && (
                            <FormHelperText>{meta.error}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                    )}
                  </Field>

                  <Field name="productTypeId">
                    {({ input, meta }) => (
                      <Grid item xs={12} md={6}>
                        <FormControl
                          fullWidth
                          error={meta.error && meta.submitFailed}
                        >
                          <SelectProductType
                            {...input}
                            label={t({ id: "evaluationFormProductTypeLabel" })}
                            textFieldProps={{
                              error: meta.error && meta.submitFailed,
                            }}
                          />
                          {meta.error && meta.submitFailed && (
                            <FormHelperText>{meta.error}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                    )}
                  </Field>

                  <Field name="productId">
                    {({ input, meta }) => (
                      <Grid item xs={12}>
                        <FormControl
                          fullWidth
                          error={meta.error && meta.submitFailed}
                        >
                          <SelectProduct
                            {...input}
                            label={t({
                              id: "evaluationFormProductLabel",
                            })}
                            organizationId={organizationId}
                            searchByName={true}
                            textFieldProps={{
                              error: meta.error && meta.submitFailed,
                            }}
                          />
                          {meta.error && meta.submitFailed && (
                            <FormHelperText>{meta.error}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                    )}
                  </Field>

                  <Field name="productYearId">
                    {({ input, meta }) => (
                      <Grid item xs={6}>
                        <FormControl
                          fullWidth
                          error={meta.error && meta.submitFailed}
                        >
                          <SelectProductYear
                            {...input}
                            label={t({
                              id: "evaluationFormProductYearLabel",
                            })}
                            textFieldProps={{
                              error: meta.error && meta.submitFailed,
                            }}
                          />
                          {meta.error && meta.submitFailed && (
                            <FormHelperText>{meta.error}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                    )}
                  </Field>

                  <Field name="productVersionId">
                    {({ input, meta }) => (
                      <Grid item xs={6}>
                        <FormControl
                          fullWidth
                          error={meta.error && meta.submitFailed}
                        >
                          <SelectProductVersion
                            {...input}
                            label={t({
                              id: "evaluationFormProductVersionLabel",
                            })}
                            textFieldProps={{
                              error: meta.error && meta.submitFailed,
                            }}
                          />
                          {meta.error && meta.submitFailed && (
                            <FormHelperText>{meta.error}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                    )}
                  </Field>

                  <Field name="skuId" validate={validateSkuAndSkuAvailability}>
                    {({ input, meta }) => (
                      <Grid item xs={12}>
                        <FormControl
                          fullWidth
                          error={meta.error && meta.submitFailed}
                          required={isSkuRequired}
                        >
                          <SelectSku
                            organizationId={evaluationOrganizationId}
                            {...input}
                            label={t({
                              id: "evaluationFormSkuLabel",
                            })}
                            textFieldProps={{
                              error: meta.error && meta.submitFailed,
                              required: isSkuRequired,
                            }}
                          />
                          {meta.error && meta.submitFailed && (
                            <FormHelperText>{meta.error}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                    )}
                  </Field>

                  {evaluationType === EvaluationTypeIdConstants.Exam && (
                    <>
                      <Grid item xs={6}>
                        <Field name="passingScore" type="number" required>
                          {({ input, meta }) => (
                            <FormControl
                              fullWidth
                              error={meta.error && meta.submitFailed}
                            >
                              <TextField
                                variant="standard"
                                fullWidth
                                label={t({
                                  id: "passingScoreLabel",
                                })}
                                error={meta.error && meta.submitFailed}
                                InputLabelProps={{ required: true }}
                                helperText={
                                  meta.error && meta.submitFailed && meta.error
                                }
                                {...input}
                              />
                            </FormControl>
                          )}
                        </Field>
                      </Grid>

                      <Grid item xs={6}>
                        <Field
                          name="attemptsAllowed"
                          type="text"
                          parse={parseInteger}
                        >
                          {({ input, meta }) => (
                            <FormControl
                              fullWidth
                              error={meta.error && meta.submitFailed}
                            >
                              <TextField
                                variant="standard"
                                fullWidth
                                label={t({
                                  id: "evaluationFormAttemptsAllowedLabel",
                                })}
                                error={meta.error && meta.submitFailed}
                                helperText={
                                  meta.error && meta.submitFailed && meta.error
                                }
                                {...input}
                              />
                            </FormControl>
                          )}
                        </Field>
                      </Grid>

                      <Field name="enablePrintReference" type="checkbox">
                        {({ input }) => (
                          <Grid item xs={12} md={4}>
                            <Box sx={styles.lineContainer}>
                              <PrintReferenceCheckbox
                                checked={input.checked}
                                onChange={input.onChange}
                                formChange={change}
                                organizationId={evaluationOrganizationId!}
                              />
                            </Box>
                          </Grid>
                        )}
                      </Field>
                    </>
                  )}

                  {evaluationType === EvaluationTypeIdConstants.Survey && (
                    <Grid item xs={12} md={6}>
                      <Field
                        name="daysAfterCourse"
                        type="text"
                        parse={parseInteger}
                      >
                        {({ input, meta }) => (
                          <FormControl
                            fullWidth
                            error={meta.error && meta.submitFailed}
                          >
                            <TextField
                              variant="standard"
                              fullWidth
                              label={t({
                                id: "evaluationFormDayAfterCourseLabel",
                              })}
                              error={meta.error && meta.submitFailed}
                              helperText={
                                meta.error && meta.submitFailed && meta.error
                              }
                              {...input}
                            />
                          </FormControl>
                        )}
                      </Field>
                    </Grid>
                  )}

                  <Field name="hasConfidenceLevels" type="checkbox">
                    {({ input }) => (
                      <Grid item xs={12} md={6} style={{ display: "flex" }}>
                        <Box sx={styles.lineContainer}>
                          <FormLabel>
                            <T id="addConfidenceLevelsLabel" />
                          </FormLabel>
                          <Checkbox {...input} />
                          <Tooltip
                            title={t({
                              id: "questionConfidenceLevelsAdviceMessage",
                            })}
                          >
                            <InfoIcon />
                          </Tooltip>
                        </Box>
                      </Grid>
                    )}
                  </Field>

                  <Grid item xs={12}>
                    <Box mt={1} />
                    <FieldArray name="questions" nonZeroArray>
                      {({ fields, meta }) => (
                        <FormControl fullWidth>
                          <FormLabel
                            error={meta.error && meta.submitFailed}
                            required
                          >
                            <T id="evaluationFormQuestionsLabel" />
                          </FormLabel>
                          <Box mt={1} />
                          <EvaluationQuestionsTable
                            fields={fields}
                            evaluationOrganizationId={
                              values.organizationId as string
                            }
                          />
                          {meta.error && meta.submitFailed && (
                            <FormHelperText error>{meta.error}</FormHelperText>
                          )}
                        </FormControl>
                      )}
                    </FieldArray>
                    {values.questions?.some(isImportedQuestion) && (
                      <Alert severity="warning">
                        <T id="evaluationFormImportedQuestionsWarning" />
                      </Alert>
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <QuestionSearch
                      organizationId={values.organizationId as string}
                      onAddQuestion={onAddQuestion}
                    />
                    {isEvaluationTypeSet ? (
                      <Box display="flex" columnGap={2}>
                        <Button
                          onClick={onCreateQuestion}
                          variant="outlined"
                          size="small"
                        >
                          <T id="evaluationFormCreateQuestionButton" />
                        </Button>
                        <Button
                          onClick={onImportQuestions}
                          variant="outlined"
                          size="small"
                        >
                          <T id="evaluationFormImportQuestionsButton" />
                        </Button>
                      </Box>
                    ) : (
                      <Alert severity="warning">
                        <T id="evaluationTypeSetMessage" />
                      </Alert>
                    )}
                  </Grid>

                  {submitError && (
                    <Alert sx={styles.feedback} severity="error">
                      {submitError}
                    </Alert>
                  )}
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button disabled={submitting} onClick={onCancel}>
                  {t({ id: "cancelButton" })}
                </Button>

                <Button
                  variant="contained"
                  type="submit"
                  color="primary"
                  disabled={submitting || fetchedEvaluationTypes.isLoading}
                >
                  {submitLabel}
                </Button>
              </DialogActions>

              <ConfirmDialog
                open={showInactiveConfirmation}
                dialogTitleKey="evaluationFormCreateTitle"
                contentKey="evaluationInactiveConfirmationMessage"
                confirmLabel="evaluationFormCreateButton"
                onCancel={showInactiveConfirmationActions.setFalse}
                handleConfirm={() => {
                  onSubmit(values);
                  showInactiveConfirmationActions.setFalse();
                }}
              />
            </form>
          );
        }}
      />
    ),
    [
      onSubmit,
      init,
      calculator,
      t,
      fetchedEvaluationTypes.data,
      fetchedEvaluationTypes.isLoading,
      evaluationOrganizationId,
      mode,
      isEvaluationTypeSet,
      styles.feedback,
      styles.lineContainer,
      onCancel,
      submitLabel,
      showInactiveConfirmation,
      showInactiveConfirmationActions,
      createOpenActions,
      importOpenActions,
      isEvaluationTypeSetActions,
      organizationId,
    ],
  );

  return (
    <>
      {form}

      <Dialog open={createOpen} disableEnforceFocus maxWidth="sm" fullWidth>
        <CreateQuestionForm
          organizationId={createQuestionOrganizationId.current!}
          onQuestionCreated={createQuestionAction.current!}
          onCancel={() => {
            createQuestionAction.current = null;
            createOpenActions.setFalse();
          }}
        />
      </Dialog>

      <EvaluationImportDialog
        open={importOpen}
        onClose={() => {
          importQuestionsAction.current = null;
          importOpenActions.setFalse();
        }}
        onImport={importQuestionsAction.current!}
      />

      {duplicateEvaluationInfo && (
        <Dialog
          open={showDuplicateDialog}
          disableEnforceFocus
          onClose={showDuplicateDialogActions.setFalse}
          maxWidth="sm"
        >
          <DialogTitle>
            <T id="duplicateEvaluationDetectedTitle" />
          </DialogTitle>
          <DialogContent dividers>
            <Typography>
              <T id="duplicateEvaluationDetectedMessage" />
            </Typography>
            <br />
            <Typography>
              <T id="duplicateEvaluationDetectedInstruction" />
            </Typography>
            <br />
            <Typography>
              <strong>
                <T id="evaluationNameLabel" />:{" "}
              </strong>
              {duplicateEvaluationInfo.name}
            </Typography>
            <Link
              component="button"
              onClick={(e) => {
                e.preventDefault();
                handleOpenExistingEvaluation(duplicateEvaluationInfo.id);
              }}
              sx={styles.link}
            >
              <T id="clickHereToOpenExistingEvaluation" />
            </Link>
          </DialogContent>
          <DialogActions>
            <Button
              autoFocus
              onClick={showDuplicateDialogActions.setFalse}
              color="primary"
            >
              <T id="closeButton" />
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default EvaluationForm;
